import AccountTreeOutlinedIcon from '@offline-mui/icons-material/AccountTreeOutlined'
import ContentPasteIcon from '@offline-mui/icons-material/ContentPaste'
import DeleteOutlineIcon from '@offline-mui/icons-material/DeleteOutlined'
import EventOutlinedIcon from '@offline-mui/icons-material/EventOutlined'
import LaunchIcon from '@offline-mui/icons-material/Launch'
import MergeTypeOutlinedIcon from '@offline-mui/icons-material/MergeTypeOutlined'
import PreviewIcon from '@offline-mui/icons-material/Preview'
import RocketLaunchIcon from '@offline-mui/icons-material/RocketLaunch'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@offline-mui/material'
import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { createRelease, previewRelease } from '../api/releaseManager'
import type { PullRequestInput, ReleasePreview, ReleaseRequest, ReleaseResult } from '../api/releaseManager'
import { DateField } from '../components/ui/DateField'
import EmptyState from '../components/ui/EmptyState'
import PageHeader from '../components/ui/PageHeader'
import SectionCard from '../components/ui/SectionCard'
import { FONT_MONO } from '../theme'

const MONTH_NAMES = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
]

function localDate(): string {
  const now = new Date()
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 10)
}

function formatBranchName(date: string, increment: string): string {
  const [, month, day] = date.split('-')
  return `uat-release-prod-${day || 'dd'}-${MONTH_NAMES[Number(month) - 1] || 'mmm'}-${increment || '0'}`
}

function identifies(
  input: PullRequestInput,
  candidate: { repository: string; number: number },
): boolean {
  const match = input.url.match(
    /github\.com\/[^/]+\/([^/]+)\/pull\/(\d+)/i,
  )
  return (
    match?.[1]?.toLowerCase() === candidate.repository.toLowerCase() &&
    Number(match[2]) === candidate.number
  )
}

function MissingTokenHelp() {
  return (
    <Alert severity="error">
      <AlertTitle>GitHub token setup required</AlertTitle>
      <Box component="ol" sx={{ my: 0, pl: 2.5 }}>
        <li>
          Open personal GitHub <strong>Settings → Credentials → Personal access
          tokens (classic)</strong>.
        </li>
        <li>
          Generate a token with the <strong>repo</strong> scope.
        </li>
        <li>
          If required, use <strong>Configure SSO</strong> to authorize the
          token for the organization.
        </li>
        <li>
          Set the API server environment variable{' '}
          <code>GitHub__Token</code>, then restart the Release Manager API.
        </li>
      </Box>
      <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
        The API performs GitHub actions as the token owner. Never paste the
        token into this page or commit it.
      </Typography>
    </Alert>
  )
}

function SummaryRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  )
}

export default function ReleaseManagerPage() {
  const [date, setDate] = useState(localDate)
  const [increment, setIncrement] = useState('')
  const [requestedBy, setRequestedBy] = useState(
    () => localStorage.getItem('uat-release-requested-by') || '',
  )
  const [urlInput, setUrlInput] = useState('')
  const [pullRequests, setPullRequests] = useState<PullRequestInput[]>([])
  const [preview, setPreview] = useState<ReleasePreview | null>(null)
  const [result, setResult] = useState<ReleaseResult | null>(null)
  const [busy, setBusy] = useState<'preview' | 'create' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const branchName = useMemo(
    () => preview?.branchName || formatBranchName(date, increment),
    [date, increment, preview],
  )

  const invalidatePreview = () => {
    setPreview(null)
    setResult(null)
    setError(null)
  }

  const buildRequest = (): ReleaseRequest => ({
    date,
    increment: increment === '' ? null : Number(increment),
    requestedBy: requestedBy.trim(),
    pullRequests,
  })

  const addUrls = () => {
    const urls = urlInput
      .split(/[\s,]+/)
      .map((value) => value.trim())
      .filter(Boolean)
    if (!urls.length) return

    const existing = new Set(
      pullRequests.map((item) => item.url.toLowerCase()),
    )
    setPullRequests((items) => [
      ...items,
      ...urls
        .filter((url) => !existing.has(url.toLowerCase()))
        .map((url) => ({ url })),
    ])
    setUrlInput('')
    invalidatePreview()
  }

  const removeUrl = (index: number) => {
    setPullRequests((items) =>
      items.filter((_, itemIndex) => itemIndex !== index),
    )
    invalidatePreview()
  }

  const handlePreview = async () => {
    setBusy('preview')
    setError(null)
    setResult(null)
    try {
      const nextPreview = await previewRelease(buildRequest())
      setPreview(nextPreview)
      if (increment === '') setIncrement(String(nextPreview.increment))
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : 'Release preview failed',
      )
    } finally {
      setBusy(null)
    }
  }

  const handleCreate = async () => {
    if (!preview?.canCreate) return
    const confirmed = window.confirm(
      `Create ${preview.branchName} in ${preview.pullRequests.length} repositories?`,
    )
    if (!confirmed) return

    setBusy('create')
    setError(null)
    localStorage.setItem('uat-release-requested-by', requestedBy.trim())
    try {
      setResult(await createRelease(buildRequest()))
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : 'Branch creation failed',
      )
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Tooling"
        title="Release Manager"
        description="Create coordinated UAT branches from production pull request heads. Preview is read-only; creation never merges, deletes, or force-pushes."
        actions={
          <Chip
            icon={<AccountTreeOutlinedIcon />}
            label={branchName}
            variant="outlined"
            color="primary"
            sx={{ fontFamily: FONT_MONO, fontWeight: 500, height: 32 }}
          />
        }
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          alignItems: 'start',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 360px' },
        }}
      >
        <Stack spacing={3} sx={{ minWidth: 0 }}>
          <SectionCard
            title="Release details"
            subtitle="The next shared increment starts at zero and is calculated during preview."
            icon={<EventOutlinedIcon />}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: '1fr 1fr',
                  md: '1fr 0.7fr 1.4fr',
                },
                gap: 2,
              }}
            >
              <DateField
                label="Release date"
                value={date}
                onChange={(value) => {
                  setDate(value)
                  invalidatePreview()
                }}
              />
              <TextField
                label="Increment"
                type="number"
                value={increment}
                placeholder="Auto"
                onChange={(event) => {
                  setIncrement(event.target.value)
                  invalidatePreview()
                }}
                slotProps={{ htmlInput: { min: 0 }, inputLabel: { shrink: true } }}
              />
              <TextField
                label="Requested by"
                value={requestedBy}
                placeholder="Name or email"
                onChange={(event) => {
                  setRequestedBy(event.target.value)
                  invalidatePreview()
                }}
              />
            </Box>
          </SectionCard>

          <SectionCard
            title="Pull requests"
            subtitle="Paste one or more GitHub PR URLs. Only one PR per repository is allowed."
            icon={<MergeTypeOutlinedIcon />}
            action={
              <Chip
                size="small"
                color={pullRequests.length ? 'primary' : 'default'}
                label={`${pullRequests.length} added`}
              />
            }
          >
            <Stack spacing={2}>
              <TextField
                multiline
                minRows={4}
                value={urlInput}
                onChange={(event) => setUrlInput(event.target.value)}
                placeholder={
                  'https://github.com/redlabelvacations/repository/pull/123\nOne URL per line'
                }
                slotProps={{ input: { sx: { fontFamily: FONT_MONO, fontSize: 13 } } }}
              />
              <Button
                variant="outlined"
                startIcon={<ContentPasteIcon />}
                onClick={addUrls}
                disabled={!urlInput.trim()}
                sx={{ alignSelf: 'flex-start' }}
              >
                Add URLs
              </Button>

              <Divider />

              {pullRequests.length === 0 ? (
                <EmptyState
                  compact
                  icon={<MergeTypeOutlinedIcon />}
                  title="No pull requests yet"
                  description="Add at least one PR URL above, then run a preview to resolve titles, branches and head commits."
                />
              ) : (
                <Stack divider={<Divider />}>
                  {pullRequests.map((item, index) => {
                    const resolved = preview?.pullRequests.find((candidate) =>
                      identifies(item, candidate),
                    )
                    return (
                      <Stack
                        key={`${item.url}-${index}`}
                        direction="row"
                        spacing={1.5}
                        sx={{ alignItems: 'flex-start', py: 1.5 }}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 2,
                            flexShrink: 0,
                            display: 'grid',
                            placeItems: 'center',
                            bgcolor: 'grey.100',
                            color: 'text.secondary',
                            '& svg': { fontSize: 18 },
                          }}
                        >
                          <MergeTypeOutlinedIcon />
                        </Box>
                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              wordBreak: 'break-word',
                              fontFamily: resolved ? undefined : FONT_MONO,
                              fontSize: resolved ? undefined : 13,
                            }}
                          >
                            {resolved?.title || item.url}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              fontFamily: FONT_MONO,
                              display: 'block',
                              mt: 0.25,
                            }}
                          >
                            {resolved
                              ? `${resolved.repository} #${resolved.number} · ${resolved.sourceBranch} · ${resolved.headSha.slice(0, 8)}`
                              : 'Waiting for preview'}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flexShrink: 0 }}>
                          {resolved ? (
                            <Chip
                              size="small"
                              color={resolved.merged ? 'warning' : 'success'}
                              label={resolved.merged ? 'Merged' : resolved.state}
                            />
                          ) : null}
                          <Tooltip title="Remove">
                            <IconButton
                              size="small"
                              aria-label="Remove pull request"
                              onClick={() => removeUrl(index)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    )
                  })}
                </Stack>
              )}
            </Stack>
          </SectionCard>
        </Stack>

        <Box sx={{ position: { lg: 'sticky' }, top: { lg: 88 } }}>
          <SectionCard title="Preview & create" subtitle="Validate first, then create.">
            <Stack spacing={2.5}>
              <Box
                sx={{
                  p: 1.75,
                  borderRadius: 2.5,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block' }}>
                  Branch
                </Typography>
                <Typography
                  sx={{
                    fontFamily: FONT_MONO,
                    fontSize: 13,
                    fontWeight: 600,
                    wordBreak: 'break-all',
                    mt: 0.5,
                  }}
                >
                  {branchName}
                </Typography>
              </Box>

              <Stack spacing={1.25} divider={<Divider />}>
                <SummaryRow label="Pull requests" value={pullRequests.length} />
                <SummaryRow label="Increment" value={increment === '' ? 'Auto' : increment} />
                <SummaryRow label="Requested by" value={requestedBy.trim() || '—'} />
              </Stack>

              {preview ? (
                <Stack spacing={1.5}>
                  <Alert severity={preview.canCreate ? 'success' : 'warning'}>
                    <AlertTitle>
                      {preview.canCreate ? 'Ready to create' : 'Action required'}
                    </AlertTitle>
                    {preview.pullRequests.length} validated PR
                    {preview.pullRequests.length === 1 ? '' : 's'} · increment{' '}
                    {preview.increment}
                  </Alert>
                  {preview.issues.map((issue) =>
                    issue.code === 'missing_token' ? (
                      <MissingTokenHelp key={issue.code} />
                    ) : (
                      <Alert
                        severity={issue.level}
                        key={`${issue.code}-${issue.repository || ''}`}
                      >
                        {issue.message}
                      </Alert>
                    ),
                  )}
                </Stack>
              ) : null}

              <Stack spacing={1.25}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={
                    busy === 'preview' ? (
                      <CircularProgress size={18} />
                    ) : (
                      <PreviewIcon />
                    )
                  }
                  disabled={busy !== null || pullRequests.length === 0}
                  onClick={() => void handlePreview()}
                >
                  Preview release
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={
                    busy === 'create' ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <RocketLaunchIcon />
                    )
                  }
                  disabled={busy !== null || !preview?.canCreate}
                  onClick={() => void handleCreate()}
                >
                  Create UAT branches
                </Button>
              </Stack>
            </Stack>
          </SectionCard>
        </Box>
      </Box>

      {result ? (
        <SectionCard
          title="Release audit"
          subtitle={`${result.requestedBy} · ${new Date(result.createdAt).toLocaleString()}`}
          icon={<RocketLaunchIcon />}
          action={
            <Chip
              label={result.branchName}
              variant="outlined"
              sx={{ fontFamily: FONT_MONO, fontWeight: 500 }}
            />
          }
          sx={{ mt: 3 }}
        >
          <Stack divider={<Divider />}>
            {result.results.map((item) => {
              const ok = item.status === 'created' || item.status === 'unchanged'
              return (
                <Stack
                  key={item.repository}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  sx={{ py: 1.5, alignItems: { sm: 'center' } }}
                >
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <Typography sx={{ fontWeight: 600 }}>{item.repository}</Typography>
                      <Chip size="small" color={ok ? 'success' : 'error'} label={item.status} />
                    </Stack>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', fontFamily: FONT_MONO, display: 'block', mt: 0.25 }}
                    >
                      PR #{item.pullRequestNumber} · {item.headSha.slice(0, 8)} · {item.message}
                    </Typography>
                  </Box>
                  {item.branchUrl ? (
                    <Link
                      href={item.branchUrl}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}
                    >
                      View branch <LaunchIcon sx={{ fontSize: 14 }} />
                    </Link>
                  ) : null}
                </Stack>
              )
            })}
          </Stack>
        </SectionCard>
      ) : null}
    </>
  )
}
